/**
 * Backup Command Center API client (Spec 201 — US5)
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import apiClient from '../services/api';

export type BackupEntityType = 'lakehouse' | 'warehouse';

export interface KindStat {
  total: number;
  succeeded: number;
  partial: number;
  failed: number;
}

/** Spec 214 D3: strict success rate = succeeded ÷ finished (Partial is NOT success and has
 *  its own bucket); null = no finished runs — render "no runs", never a default 100%/0%. */
export interface Overview {
  total: number;
  succeeded: number;
  partial: number;
  failed: number;
  cancelled: number;
  inflight: number;
  finished: number;
  successRate: number | null;
  byType: Record<'lakehouse' | 'warehouse' | 'workspace', KindStat>;
  /** Spec 298 FR-004a: the tenant's launchable schedules with an occurrence in the same 30-day window. */
  dueSchedules: number;
  /** Spec 298 FR-004a: the schedules whose cron or zone does not parse, by name; never counted as not due. */
  unparsedSchedules: string[];
}

export interface CoverageGap {
  entityType: BackupEntityType;
  entityId: string;
  name: string | null;
  workspaceName: string | null;
  createdAt: string | null;
  sizeGb: number | null;
  /** Set when the last collection did not size the item: sizeGb is then its last known size (T159 item 32). */
  sizeMeasurementError?: string | null;
  tableCount: number | null;
  reason: string; // 'No plan' | 'Plan paused'
}

export interface Coverage {
  protected: number;
  total: number;
  gaps: CoverageGap[];
}

export interface ReadinessRow {
  entityType: BackupEntityType;
  entityId: string;
  name: string | null;
  workspaceName: string | null;
  planName: string | null;
  /** 'active' | 'paused' | 'draft' | 'archived' | … (Spec 292 D4: at-risk and within-RPO are judged over 'active' only). */
  planStatus: string | null;
  protectionTier: string | null;
  rpoHours: number | null;
  rtoHours: number | null;
  lastSuccessfulBackup: string | null;
  expectedIntervalMs: number | null;
  atRisk: boolean;
  scheduleMeetsRpo: boolean | null;
}

export interface TrendPoint {
  date: string; // US Eastern day bucket (Spec 214 — house rule)
  succeeded: number;
  partial: number; // Spec 214 FR-013: Partial is a first-class series (it used to vanish)
  failed: number;
  total: number;
  lhSucceeded: number;
  lhPartial: number;
  lhFailed: number;
  whSucceeded: number;
  whPartial: number;
  whFailed: number;
  wsSucceeded: number;
  wsPartial: number;
  wsFailed: number;
}

export interface BackupTuning {
  rpoToleranceMultiplier: number;
  maxConcurrentJobs: number;
}

export interface DashboardInsights {
  protectionByClass: Array<{
    cls: 'lakehouse' | 'warehouse' | 'workspace';
    entities: number; // D1 population (exclusions netted)
    withPlan: number; // D2: ACTIVE-plan targets only (Spec 214 — paused = gap)
    withPoints: number;
    /** D2 ∩ points: covered by an ACTIVE plan AND has ≥1 recovery point. */
    withPlanAndPoints: number;
    recoveryPoints: number;
    lastBackupAt: string | null;
    // Spec 211/214 — the exclusion-aware chip contract, now ALWAYS present (Spec 214 FR-005:
    // insights carries it; the dead coverage-by-class endpoint was deleted).
    total: number;
    protected: number;
    pending: number;
    excluded: number;
  }>;
  workspaceDr: {
    protectedWorkspaces: number;
    enrolledWorkspaces: number;
    latestCaptureAt: string | null;
    capturedItems: number;
    pointerItems: number;
    unsupportedItems: number;
    /** Null when nothing was captured (Spec 298 FR-004). */
    dedupPct: number | null;
    itemsByType: Array<{ type: string; count: number }>;
  };
  storage: {
    totalBytes: number;
    /** Assets in totalBytes the last collection did not size, counted at their last known size (T159 item 32). */
    sizeNotMeasuredCount?: number;
    byAccount: Array<{ account: string; bytes: number; assets: number; sizeNotMeasuredCount?: number }>;
    growth: Array<{ date: string; bytes: number; forecast: boolean }>;
  };
  recoveryDepth: {
    totalPoints: number;
    lakehousePoints: number;
    warehousePoints: number;
    definitionPoints: number;
    oldestPointAt: string | null;
    retentionDays: number | null;
  };
  dataReduction: {
    fullRuns: number;
    incrementalRuns: number;
    /** Null when no run finished in the window (Spec 298 FR-004). */
    incrementalPct: number | null;
  };
  compliance: {
    rtoMeasured: number;
    rtoMet: number;
    /** Null when no restore time was measured (Spec 298 FR-004). */
    rtoPct: number | null;
    avgObservedRtoSeconds: number | null;
    tiers: Array<{ tier: string; rpoHours: number; rtoHours: number; assets: number }>;
  };
}

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup/dashboard';

export const backupDashboardApi = {
  overview: async (): Promise<Overview> => (await apiClient.get<Wrapped<Overview>>(`${BASE}/overview`)).data,
  coverage: async (): Promise<Coverage> => (await apiClient.get<Wrapped<Coverage>>(`${BASE}/coverage`)).data,
  readiness: async (): Promise<ReadinessRow[]> => (await apiClient.get<Wrapped<ReadinessRow[]>>(`${BASE}/readiness`)).data,
  trend: async (days = 14): Promise<TrendPoint[]> =>
    (await apiClient.get<Wrapped<TrendPoint[]>>(`${BASE}/trend?days=${days}`)).data,
  insights: async (): Promise<DashboardInsights> => (await apiClient.get<Wrapped<DashboardInsights>>(`${BASE}/insights`)).data,
  settings: async (): Promise<BackupTuning> => (await apiClient.get<Wrapped<BackupTuning>>(`${BASE}/settings`)).data,
  updateSettings: async (patch: Partial<BackupTuning>): Promise<BackupTuning> =>
    (await apiClient.put<Wrapped<BackupTuning>>(`${BASE}/settings`, patch)).data,
};
